import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Loader2, CheckCircle2, XCircle, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";

const MAX_POLLS = 8;
const POLL_MS = 2000;

export default function PaymentStatusPage({ outcome }) {
  const [params] = useSearchParams();
  const session_id = params.get("session_id");
  const nav = useNavigate();
  const { setUserDirect } = useAuth();
  const queryClient = useQueryClient();
  const [state, setState] = useState(outcome === "cancel" ? "cancelled" : "loading");
  const pollRef = useRef(0);

  useEffect(() => {
    if (outcome === "cancel" || !session_id) return;
    const check = async () => {
      try {
        const { data } = await api.get(`/payments/status/${session_id}`);
        if (data.payment_status === "paid") {
          setState("paid");
          // Rafraîchit l'utilisateur pour récupérer le nouveau palier d'abonnement
          try {
            const { data: me } = await api.get("/auth/me");
            setUserDirect(me);
            queryClient.invalidateQueries();
          } catch {}
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setState("failed"); return;
        }
        if (pollRef.current >= MAX_POLLS) { setState("pending"); return; }
        pollRef.current++;
        setTimeout(check, POLL_MS);
      } catch {
        setState("failed");
      }
    };
    check();
  }, [session_id, outcome]);

  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-6 py-12">
      <Card className="p-10 bg-card border-white/5 max-w-md text-center" data-testid="payment-status-card">
        {state === "loading" && (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-emerald-400 mx-auto mb-4" />
            <h1 className="font-display font-black text-2xl">Vérification du paiement…</h1>
            <p className="text-zinc-400 mt-2 text-sm">Ne fermez pas cette page, c'est presque terminé.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-4" />
            <h1 className="font-display font-black text-2xl">Bienvenue à bord</h1>
            <p className="text-zinc-400 mt-2 text-sm">Votre abonnement est actif. Profitez des contenus premium.</p>
            <Button asChild className="mt-6 bg-emerald-500 text-black hover:bg-emerald-300" data-testid="payment-continue">
              <Link to="/">Explorer <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
          </>
        )}
        {(state === "cancelled" || state === "failed" || state === "pending") && (
          <>
            <XCircle className={`w-14 h-14 mx-auto mb-4 ${state === "cancelled" ? "text-zinc-500" : "text-rose-400"}`} />
            <h1 className="font-display font-black text-2xl">
              {state === "cancelled" ? "Paiement annulé" : state === "pending" ? "En attente" : "Paiement échoué"}
            </h1>
            <p className="text-zinc-400 mt-2 text-sm">
              {state === "pending" ? "Le paiement peut prendre quelques minutes. Vous serez notifié quand il sera confirmé." : "Aucun montant n'a été prélevé."}
            </p>
            <Button asChild variant="outline" className="mt-6 border-white/10 bg-transparent" data-testid="payment-retry">
              <Link to="/upgrade">Retour aux offres</Link>
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
